import { type RefObject, useCallback, useEffect, useRef } from "react";
import { hasAuthParams, type AuthContextProps } from "react-oidc-context";
import { type SigninState } from "@/auth";

type AccessTokenChangedHandler = (token?: string) => void;
type SigninStateBuilder = () => SigninState | undefined;

interface UseAuthSessionManagementOptions {
  onAccessTokenChanged?: AccessTokenChangedHandler;
  buildSigninState?: SigninStateBuilder;
}

interface AuthGuards {
  signin: boolean;
  reLogin: boolean;
  renewPending: boolean;
}

export default function useAuthSessionManagement(
  auth: AuthContextProps,
  options: UseAuthSessionManagementOptions = {},
): void {
  const guardsRef = useRef<AuthGuards>({
    signin: false,
    reLogin: false,
    renewPending: false,
  });
  const triggerReLogin = useTriggerReLogin(
    auth,
    guardsRef,
    options.buildSigninState,
  );

  useTokenSync(auth, options.onAccessTokenChanged);
  useAutoSignin(auth, guardsRef, options.buildSigninState);
  useResetGuards(auth, guardsRef);
  useSilentRenewRecovery(auth, guardsRef, triggerReLogin);
}

function useTokenSync(
  auth: AuthContextProps,
  onAccessTokenChanged?: AccessTokenChangedHandler,
) {
  useEffect(() => {
    onAccessTokenChanged?.(auth.user?.access_token);
  }, [auth.user?.access_token, onAccessTokenChanged]);
}

function useTriggerReLogin(
  auth: AuthContextProps,
  guardsRef: RefObject<AuthGuards>,
  buildSigninState?: SigninStateBuilder,
) {
  return useCallback(async () => {
    if (guardsRef.current.reLogin) return;

    guardsRef.current = { signin: false, reLogin: true, renewPending: false };
    auth.stopSilentRenew();
    await auth.removeUser().catch(() => {});
    const state = buildSigninState?.();
    void auth.signinRedirect(state !== undefined ? { state } : undefined);
  }, [auth, buildSigninState, guardsRef]);
}

function useAutoSignin(
  auth: AuthContextProps,
  guardsRef: RefObject<AuthGuards>,
  buildSigninState?: SigninStateBuilder,
) {
  useEffect(() => {
    const shouldSkip =
      guardsRef.current.signin ||
      auth.isLoading ||
      auth.isAuthenticated ||
      auth.activeNavigator ||
      hasAuthParams();
    if (shouldSkip) return;

    guardsRef.current.signin = true;
    const state = buildSigninState?.();
    void auth.signinRedirect(state !== undefined ? { state } : undefined);
  }, [auth, buildSigninState, guardsRef]);
}

function useResetGuards(
  auth: AuthContextProps,
  guardsRef: RefObject<AuthGuards>,
) {
  useEffect(() => {
    if (auth.isAuthenticated) {
      guardsRef.current = {
        signin: false,
        reLogin: false,
        renewPending: false,
      };
    }
  }, [auth.isAuthenticated, guardsRef]);
}

function useSilentRenewRecovery(
  auth: AuthContextProps,
  guardsRef: RefObject<AuthGuards>,
  triggerReLogin: () => Promise<void>,
) {
  useEffect(() => {
    const handleFailure = (error: unknown) => {
      if (
        navigator.onLine &&
        shouldForceReLogin(error) &&
        !guardsRef.current.reLogin
      ) {
        void triggerReLogin();
        return;
      }
      guardsRef.current.renewPending = true;
    };

    const retrySilentRenew = () => {
      const skipSilentRenew =
        !guardsRef.current.renewPending ||
        auth.isLoading ||
        auth.activeNavigator;
      if (skipSilentRenew) return;
      guardsRef.current.renewPending = false;
      void auth.signinSilent().catch(handleFailure);
    };

    const unsubscribe = auth.events.addSilentRenewError((error) => {
      handleFailure(error);
      if (navigator.onLine) retrySilentRenew();
    });

    window.addEventListener("online", retrySilentRenew);
    return () => {
      unsubscribe();
      window.removeEventListener("online", retrySilentRenew);
    };
  }, [auth, guardsRef, triggerReLogin]);
}

function shouldForceReLogin(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const parts = [error.message];
  if ("error_description" in error) parts.push(String(error.error_description));
  if ("error" in error) parts.push(String(error.error));
  const details = parts.join(" ").toLowerCase();

  return /invalid_grant|invalid grant|invalid_request|login_required|interaction_required|consent_required|invalid_token|refresh token|refreshtokeninvalid|expired/.test(
    details,
  );
}
